import React, { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/solid'
import Accordion from '../Accordion/Accordion'
import CheckBox from '../CheckBox/CheckBox'
import clsx from 'clsx'
import { isNil } from 'lodash'
import { isEmpty } from 'ramda'
import Button from '../Button/Button'
import ButtonWithTooltip from '../ButtonWithTooltip/ButtonWithTooltip'
import Filter from '../../svg/Filter'
import ClickAwayListener from 'react-click-away-listener'
import useScreen from '../../hooks/useScreen'
import { useNetworkStatus } from '../../utils/networkStatus'

const defaultFilters = [
    {
        filterGrupName: 'Ownership',
        filters: [
            {
                filterName: 'Rented',
                checked: false,
            },
            {
                filterName: 'Owned',
                checked: false,
            },
        ],
    },
    {
        filterGrupName: 'Equipment',
        filters: [
            {
                filterName: 'Forkit',
                checked: false,
            },
            {
                filterName: 'Forkit 2sd',
                checked: false,
            },
        ],
    },
    {
        filterGrupName: 'Location',
        filters: [
            {
                filterName: 'Alabama Power 321',
                checked: false,
            },
            {
                filterName: 'Ct 3325 TrT City - California',
                checked: false,
            },
        ],
    },
]

const FilterTag = ({ text, onRemove }) => (
    <div className="w-auto h-auto py-1 px-2 max-w-full flex items-center justify-center bg-grayBackground text-black60">
        <XIcon
            role="button"
            className="w-4 mr-1 text-textDarkGray"
            onClick={onRemove}
        />
        <p className="truncate max-w-full">{text}</p>
    </div>
)

FilterTag.propTypes = {
    text: PropTypes.string,
    onRemove: PropTypes.func,
}

const FilterModal = ({
    open,
    setOpen,
    filtersGroup = defaultFilters,
    onFilter,
    isSmallScreen = false,
    data = [],
}) => {
    const { isMobile } = useScreen()
    const { isOffline } = useNetworkStatus()
    const [allFilters, setAllFilters] = useState(
        filtersGroup?.map((filterG, index) => ({
            ...filterG,
            id: index,
            filters: filterG?.filters?.map((filter, indexFilter) => ({
                ...filter,
                id: indexFilter,
            })),
        })),
    )

    useEffect(() => {
        if (filtersGroup?.length > 0) {
            setAllFilters(
                filtersGroup?.map((filterG, index) => ({
                    ...filterG,
                    id: index,
                    filters: filterG?.filters?.map((filter, indexFilter) => ({
                        ...filter,
                        id: indexFilter,
                    })),
                })),
            )
        }
    }, [filtersGroup])

    const [currentFilters, setCurrentFilters] = useState(allFilters)

    const hasDifferences =
        JSON.stringify(currentFilters) !== JSON.stringify(allFilters)

    const handleViewResults = () => {
        onFilter && onFilter(allFilters)
        setCurrentFilters(allFilters)
        setOpen(false)
    }

    const filtersGroupsWithActiveFilters = allFilters?.filter(filterG => {
        const filterGHasFilter = filterG?.filters?.find(f => f.checked === true)
        return !isNil(filterGHasFilter)
    })

    const onClearAllFilters = () => {
        const newFilters = allFilters?.map(filterG => ({
            ...filterG,
            filters: filterG.filters?.map(f => ({ ...f, checked: false })),
        }))
        setAllFilters(newFilters)
        setCurrentFilters(newFilters)
        onFilter(newFilters)
    }

    const toggleFilterChecked = (filterGId, filterId) => {
        const newFilters = allFilters.map(filterG => {
            if (filterG?.id === filterGId) {
                return {
                    ...filterG,
                    filters: filterG?.filters?.map(filter => {
                        if (filter?.id === filterId) {
                            return {
                                ...filter,
                                checked: !filter?.checked,
                            }
                        }
                        return filter
                    }),
                }
            }
            return filterG
        })
        setAllFilters(newFilters)
    }

    const getSizeOfActiveFilters = () => {
        let filtersAppliedCount = 0
        filtersGroupsWithActiveFilters?.forEach(filterG => {
            filterG?.filters?.forEach(filter => {
                if (filter?.checked === true) {
                    filtersAppliedCount++
                }
            })
        })
        return filtersAppliedCount
    }

    const sizeOfActiveFilters = getSizeOfActiveFilters()

    const getFilterCount = (filterG, filter) => {
        if (!data || data.length === 0) return null
        const count = data.filter(item => {
            if (filter?.isLocation) {
                return item[filterG?.field] == filter?.shiptoId
            } else if (filter?.filterName === 'Equipment') {
                return (
                    item[filterG?.field] === 'New Equipment' ||
                    item[filterG?.field] === 'Used Equipment'
                )
            } else {
                const valueToCompare =
                    typeof item[filterG?.field] === 'boolean'
                        ? true
                        : filter?.filterName
                return item[filterG?.field] === valueToCompare
            }
        }).length
        return count
    }

    return (
        <div className="flex flex-row items-center jusitfy-center">
            <div className="relative">
                <ButtonWithTooltip
                    variant="tertiary"
                    text={isSmallScreen && isMobile ? '' : 'Filter By'}
                    className="text-textDarkGray text-sm h-12 min-w-max"
                    icon={
                        <Filter className="text-textDarkGray noTextx md:mr-2 mr-0" />
                    }
                    onClick={() => !isOffline && setOpen(true)}
                    disabled={isOffline}
                    isOffline={isOffline}
                    position="left"
                />
                {sizeOfActiveFilters > 0 && (
                    <span className="absolute -left-1 -top-1 w-auto text-xs h-auto leading-1.5 p-2 text-white bg-textDarkerGray rounded-full flex items-center justify-center">
                        {sizeOfActiveFilters}
                    </span>
                )}
            </div>
            <Transition appear show={open} as={Fragment}>
                <div
                    className={clsx(
                        'absolute inset-0 w-full h-full z-200 transition-all duration-300',
                        open ? 'bg-modalBackground' : 'bg-transparent',
                    )}
                >
                    <ClickAwayListener onClickAway={() => setOpen(false)}>
                        <Transition.Child
                            className="h-full transition-all md:w-116 w-full border-l border-grayLines fixed right-0 top-0 bg-white z-170 duration-300 flex flex-col items-center justify-between"
                            enterFrom="opacity-30 md:-right-116 -right-full"
                            enterTo="opacity-100 right-0"
                            leaveFrom="opacity-100 right-0"
                            leaveTo="opacity-0 md:-right-116 -right-full"
                        >
                            <div className="flex flex-row items-center justify-between p-6 border-b border-grayLines w-full">
                                <p className="text text-black70 text-lg font-semibold">
                                    {'Filter By'}
                                </p>
                                <XIcon
                                    role="button"
                                    className="w-6 text-black70"
                                    onClick={() => setOpen(false)}
                                />
                            </div>
                            <div className="flex flex-col items-center justify-start w-full max-h-full overflow-auto flex-grow">
                                {!isEmpty(filtersGroupsWithActiveFilters) && (
                                    <div className="flex flex-col items-center justify-start w-full m-auto p-6 border-b border-grayLines">
                                        <div className="w-full flex items-center justify-between">
                                            <p className="text-black font-semibold text-sm">
                                                Applied Filters
                                            </p>
                                            <p
                                                role="button"
                                                onClick={() =>
                                                    onClearAllFilters()
                                                }
                                                className="text-links font-semibold text-sm hover:underline transition-all"
                                            >
                                                Clear All
                                            </p>
                                        </div>

                                        <div className="w-full flex flex-row items-center justify-start gap-2 flex-wrap flex-grow mt-2">
                                            {filtersGroupsWithActiveFilters?.map(
                                                filterG => {
                                                    return filterG?.filters?.map(
                                                        (filter, index) => {
                                                            return (
                                                                filter?.checked && (
                                                                    <FilterTag
                                                                        key={`filterTag-${index}`}
                                                                        text={
                                                                            filter?.filterName
                                                                        }
                                                                        onRemove={() =>
                                                                            toggleFilterChecked(
                                                                                filterG?.id,
                                                                                filter?.id,
                                                                            )
                                                                        }
                                                                    />
                                                                )
                                                            )
                                                        },
                                                    )
                                                },
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className="flex flex-grow flex-col items-center justify-start w-full px-4">
                                    {allFilters.map((filterG, index) => {
                                        return (
                                            <Accordion
                                                hasBorder={
                                                    index !==
                                                    allFilters?.length - 1
                                                }
                                                key={`filterGroup-${filterG?.id}`}
                                                text={filterG.filterGrupName}
                                                content={
                                                    <ul className="mt-4 flex flex-col items-start justify-start">
                                                        {filterG?.filters?.map(
                                                            (
                                                                filter,
                                                                indexF,
                                                            ) => {
                                                                const filterIsChecked =
                                                                    filter.checked
                                                                return (
                                                                    <li
                                                                        key={`filter-${filter?.id}`}
                                                                        className={clsx(
                                                                            indexF !==
                                                                                filterG
                                                                                    ?.filters
                                                                                    ?.length -
                                                                                    1 &&
                                                                                'mb-2',
                                                                        )}
                                                                    >
                                                                        <CheckBox
                                                                            defaultChecked={
                                                                                filterIsChecked
                                                                            }
                                                                            onChange={() =>
                                                                                toggleFilterChecked(
                                                                                    filterG?.id,
                                                                                    filter?.id,
                                                                                )
                                                                            }
                                                                            label={
                                                                                filter?.isLocation ? (
                                                                                    <span className="flex items-start gap-1 max-w-full">
                                                                                        <span className="flex-1 min-w-0">
                                                                                            {filter?.customRender()}
                                                                                        </span>
                                                                                        {data?.length >
                                                                                            0 && (
                                                                                            <span className="text-textDarkGray text-xs shrink-0 mt-1">
                                                                                                (
                                                                                                {getFilterCount(
                                                                                                    filterG,
                                                                                                    filter,
                                                                                                )}

                                                                                                )
                                                                                            </span>
                                                                                        )}
                                                                                    </span>
                                                                                ) : (
                                                                                    <>
                                                                                        {
                                                                                            filter?.filterName
                                                                                        }
                                                                                        {data?.length >
                                                                                            0 && (
                                                                                            <span className="text-textDarkGray text-xs ml-1">
                                                                                                (
                                                                                                {getFilterCount(
                                                                                                    filterG,
                                                                                                    filter,
                                                                                                )}

                                                                                                )
                                                                                            </span>
                                                                                        )}
                                                                                    </>
                                                                                )
                                                                            }
                                                                        />
                                                                    </li>
                                                                )
                                                            },
                                                        )}
                                                    </ul>
                                                }
                                            />
                                        )
                                    })}
                                </div>
                            </div>
                            <div className="p-6 w-full">
                                <Button
                                    variant="primary"
                                    text="View Results"
                                    className="w-full"
                                    disabled={!hasDifferences}
                                    onClick={() => handleViewResults()}
                                />
                            </div>
                        </Transition.Child>
                    </ClickAwayListener>
                </div>
            </Transition>
        </div>
    )
}

FilterModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    filtersGroup: PropTypes.arrayOf(PropTypes.object),
    onFilter: PropTypes.func,
    isSmallScreen: PropTypes.bool,
    data: PropTypes.array,
}

export default FilterModal
